import {
  Component,
  Input,
  signal,
  computed,
  OnInit,
  OnDestroy,
  HostListener,
  inject,
  ChangeDetectionStrategy,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  LucideAngularModule,
  Grid3X3Icon,
  ListIcon,
  Loader2Icon,
} from 'lucide-angular';
import { ProductCard } from '../product-card/product-card';
import { ProductListItem } from '../product-list-item/product-list-item';
import { ProductFilters } from '../product-filters/product-filters';
import type { Producto } from '../../../types/product.types';
import { QuickFiltersTouchbar } from '../quick-filters-touchbar/quick-filters-touchbar.component';
import { DrawerModule } from 'primeng/drawer';
import { VehicleSelector } from '../../garage/vehicle-selector/vehicle-selector';
import { VehicleRecommendation } from '../vehicle-recommendation/vehicle-recommendation';
import { VehicleService } from '../../../services/vehicle.service';
import { filterProductsByQuickFilter } from '../../../utils/products/quick-filter.utils';

@Component({
  selector: 'allas-product-list',
  changeDetection: ChangeDetectionStrategy.OnPush,

  imports: [
    CommonModule,
    FormsModule,
    LucideAngularModule,
    ProductCard,
    ProductListItem,
    ProductFilters,
    QuickFiltersTouchbar,
    DrawerModule,
    VehicleSelector,
    VehicleRecommendation,
  ],
  template: `
    <div class="min-h-screen mt-8" lang="es-HN">
      <div class="w-full">
        <!-- Header Section -->
        <ng-container [ngTemplateOutlet]="headerTemplate"></ng-container>

        <div class="flex flex-col xl:flex-row gap-2">
          <!-- Filters Sidebar -->
          <ng-container
            [ngTemplateOutlet]="filtersSidebarTemplate"
          ></ng-container>

          <!-- Filters Touchbar -->
          <ng-container
            [ngTemplateOutlet]="filtersTouchbarTemplate"
          ></ng-container>

          <!-- Filters Drawer -->
          <ng-container
            [ngTemplateOutlet]="filtersDrawerTemplate"
          ></ng-container>

          <!-- Products Section -->
          <div class="flex-1 min-w-0">
            <!-- Vehicle Components -->
            <ng-container
              [ngTemplateOutlet]="vehicleComponentsTemplate"
            ></ng-container>

            <!-- Toolbar -->
            <ng-container [ngTemplateOutlet]="toolbarTemplate"></ng-container>

            <!-- Products Display -->
            <ng-container
              [ngTemplateOutlet]="productsDisplayTemplate"
            ></ng-container>
          </div>
        </div>
      </div>
    </div>

    <!-- Templates -->
    <ng-template #headerTemplate>
      <header class="mb-8">
        <h2 class="text-3xl font-bold text-gray-900 mb-2">
          {{ categoryName || 'Productos para carro' }}
          <!---->
          <!-- @if (marca()) { de la marca {{ marca() }} } -->
          <!---->

          @if (marcaVehiculo) {
          <!---->
          para {{ marcaVehiculo }} {{ modeloVehiculo }} {{ anioVehiculo }}
          <!---->
          }
        </h2>
        @if (h1DescriptionTitle) {
        <h3 class="text-gray-500 mb-2">
          {{ h1DescriptionTitle }}
        </h3>
        }
        <p class="text-gray-600">
          {{ displayedProducts().length }} de
          {{ getTotalAvailableProducts() }} productos mostrados @if
          (hasDefaultVehicle()) {
          <span class="text-appPrimary-500 font-medium">
            (solo productos compatibles con tu vehículo)
          </span>
          }
        </p>
      </header>
    </ng-template>

    <ng-template #filtersSidebarTemplate>
      <aside class="w-72 flex-shrink-0 hidden xl:block">
        <allas-product-filters
          [products]="products"
          [currentSortBy]="sortBy()"
          (filtersChanged)="onFiltersChanged($event)"
          (sortChanged)="onSortChange($event)"
          (priceFiltersChanged)="onPriceFiltersChanged($event)"
        />
      </aside>
    </ng-template>

    <ng-template #filtersTouchbarTemplate>
      <div class="xl:hidden">
        <allas-quick-filters-touchbar
          [products]="products"
          [selectedQuickFilter]="selectedQuickFilter()"
          (openFiltersDrawer)="filtersDrawerVisible.set(true)"
          (onQuickFilterSelected)="onQuickFilterSelected($event)"
        />
      </div>
    </ng-template>

    <ng-template #filtersDrawerTemplate>
      <p-drawer
        [(visible)]="filtersDrawerVisible"
        header="Filtros"
        position="bottom"
        [style]="{ height: '90vh' }"
      >
        <allas-product-filters
          [products]="products"
          [currentSortBy]="sortBy()"
          (filtersChanged)="onFiltersChanged($event)"
          (sortChanged)="onSortChange($event)"
          (priceFiltersChanged)="onPriceFiltersChanged($event)"
        />
      </p-drawer>
    </ng-template>

    <ng-template #vehicleComponentsTemplate>
      @if (!hasDefaultVehicle()) {
      <allas-vehicle-selector />
      } @else {
      <allas-vehicle-recommendation [categoryName]="categoryName" />
      }
    </ng-template>

    <ng-template #toolbarTemplate>
      <div
        class="bg-white border border-gray-200 rounded-lg p-2 mb-2 hidden xl:block"
      >
        <div class="flex items-center justify-between">
          <span class="flex gap-2 items-center">
            <!-- Sort Options -->
            <select
              [ngModel]="sortBy()"
              (ngModelChange)="onSortChange($event)"
              class="w-56 fs-condensed border border-gray-300 rounded-md p-2 outline-none"
              aria-label="Ordenar productos por"
            >
              <option value="relevance">Destacados</option>
              <option value="price-low">Precio (menor a mayor)</option>
              <option value="price-high">Precio (mayor a menor)</option>
              <option value="rating">Valoración del cliente</option>
              <option value="best-sellers">Más vendidos</option>
            </select>

            <!-- Quick Filters -->
            <allas-quick-filters-touchbar
              [products]="products"
              [selectedQuickFilter]="selectedQuickFilter()"
              (openFiltersDrawer)="filtersDrawerVisible.set(true)"
              (onQuickFilterSelected)="onQuickFilterSelected($event)"
            />
          </span>

          <!-- View Toggle -->
          <div
            class="flex items-center space-x-2 flex-shrink-0"
            role="group"
            aria-label="Cambiar vista de productos"
          >
            <button
              (click)="setViewMode('grid')"
              [class]="
                viewMode() === 'grid'
                  ? 'bg-appPrimary-500 text-white'
                  : 'bg-gray-100 text-gray-600'
              "
              class="p-2 rounded transition-colors"
              aria-label="Vista en cuadrícula"
              [attr.aria-pressed]="viewMode() === 'grid'"
            >
              <lucide-icon [name]="gridIcon" class="w-4 h-4"></lucide-icon>
            </button>
            <button
              (click)="setViewMode('list')"
              [class]="
                viewMode() === 'list'
                  ? 'bg-appPrimary-500 text-white'
                  : 'bg-gray-100 text-gray-600'
              "
              class="p-2 rounded transition-colors"
              aria-label="Vista en lista"
              [attr.aria-pressed]="viewMode() === 'list'"
            >
              <lucide-icon [name]="listIcon" class="w-4 h-4"></lucide-icon>
            </button>
          </div>
        </div>
      </div>
    </ng-template>

    <ng-template #productsDisplayTemplate>
      @if (displayedProducts().length > 0) {
      <ng-container [ngTemplateOutlet]="productsGridTemplate"></ng-container>
      <ng-container [ngTemplateOutlet]="loadMoreTemplate"></ng-container>
      <ng-container [ngTemplateOutlet]="loadingTemplate"></ng-container>
      <ng-container [ngTemplateOutlet]="endOfResultsTemplate"></ng-container>
      } @else {
      <ng-container [ngTemplateOutlet]="noResultsTemplate"></ng-container>
      }
    </ng-template>

    <ng-template #productsGridTemplate>
      @if (viewMode() === 'grid') {
      <section
        class="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-2"
        aria-label="Productos en vista de cuadrícula"
      >
        @for (product of displayedProducts(); track product.sku) {
        <allas-product-card
          [product]="product"
          [compatibleWithVehicle]="hasDefaultVehicle()"
          [defaultVehicleAlias]="defaultVehicleAlias"
        />
        }
      </section>
      } @else {
      <section class="space-y-4" aria-label="Productos en vista de lista">
        @for (product of displayedProducts(); track product.sku) {
        <allas-product-list-item
          [product]="product"
          [compatibleWithVehicle]="hasDefaultVehicle()"
          [defaultVehicleAlias]="defaultVehicleAlias"
        />
        }
      </section>
      }
    </ng-template>

    <ng-template #loadMoreTemplate>
      @if (hasMoreProducts() && !isLoading()) {
      <div class="my-8 flex justify-center">
        <button
          (click)="loadMoreProducts()"
          class="px-8 py-3 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors flex items-center gap-2"
          aria-label="Cargar más productos"
        >
          Cargar más productos
          <lucide-icon [name]="loaderIcon" class="w-4 h-4"></lucide-icon>
        </button>
      </div>
      }
    </ng-template>

    <ng-template #loadingTemplate>
      @if (isLoading()) {
      <div class="mt-8 flex justify-center">
        <div
          class="flex items-center gap-2 text-gray-600"
          role="status"
          aria-label="Cargando productos"
        >
          <lucide-icon
            [name]="loaderIcon"
            class="w-4 h-4 animate-spin"
          ></lucide-icon>
          Cargando más productos...
        </div>
      </div>
      }
    </ng-template>

    <ng-template #endOfResultsTemplate>
      @if (!hasMoreProducts() && displayedProducts().length > 0) {
      <div class="mt-8 text-center">
        <div class="text-gray-500 border-t border-gray-200 pt-4">
          <p class="text-sm">Has visto todos los productos disponibles</p>
        </div>
      </div>
      }
    </ng-template>

    <ng-template #noResultsTemplate>
      <div class="text-center py-16">
        <div class="text-6xl mb-4" aria-hidden="true">🔍</div>
        <h2 class="text-xl font-semibold text-gray-900 mb-2">
          No se encontraron productos
        </h2>
        <p class="text-gray-600 mb-6">
          Intenta ajustar los filtros o buscar con otros criterios
        </p>
        <button
          (click)="clearAllFilters()"
          class="px-6 py-3 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors"
          aria-label="Limpiar todos los filtros aplicados"
        >
          Limpiar Filtros
        </button>
      </div>
    </ng-template>
  `,
  styles: [
    `
      :host {
        display: block;
      }
    `,
  ],
})
export class ProductList implements OnInit, OnDestroy, OnChanges {
  @Input() h1DescriptionTitle: string = '';
  @Input() products: Producto[] = [];
  @Input() categoryName: string = '';
  @Input() marcaVehiculo: string = '';
  @Input() modeloVehiculo: string = '';
  @Input() anioVehiculo: string = '';
  @Input() productsFetchedByVehicle = false;
  @Input() defaultVehicleAlias = '';

  private vehicleService = inject(VehicleService);

  readonly gridIcon = Grid3X3Icon;
  readonly listIcon = ListIcon;
  readonly loaderIcon = Loader2Icon;

  // State
  viewMode = signal<'grid' | 'list'>('grid');
  currentPage = signal(1);
  itemsPerPage = 50; // Aumentado de 12 a 50
  sortBy = signal('relevance');
  activeFilters = signal<Record<string, string[]>>({});
  priceFilters = signal<{ min?: number; max?: number }>({});
  filtersDrawerVisible = signal(false);
  isLoading = signal(false);
  selectedQuickFilter = signal<string | null>(null);

  // Computed values
  filteredProducts = computed(() => {
    let filtered = this.products;
    const filters = this.activeFilters();
    const priceFilters = this.priceFilters();
    const quickFilter = this.selectedQuickFilter();

    // Apply quick filter first (if any)
    if (quickFilter) {
      filtered = filterProductsByQuickFilter(filtered, quickFilter);
    }

    // Apply price filters
    if (priceFilters.min !== undefined || priceFilters.max !== undefined) {
      filtered = filtered.filter((product) => {
        const price = product.precio;
        if (price === null || price === undefined) return false;

        if (priceFilters.min !== undefined && price < priceFilters.min) {
          return false;
        }

        if (priceFilters.max !== undefined && price > priceFilters.max) {
          return false;
        }

        return true;
      });
    }

    // Apply attribute-based filters
    Object.entries(filters).forEach(([filterKey, values]) => {
      if (values.length > 0) {
        filtered = filtered.filter((product) => {
          return (
            this.productMatchesAttributeFilter(product, filterKey, values) ||
            this.productMatchesBrandFilter(product, values)
          );
        });
      }
    });

    // Apply sorting
    filtered = this.sortProducts(filtered);

    return filtered;
  });

  displayedProducts = computed(() => {
    const endIndex = this.currentPage() * this.itemsPerPage;
    return this.filteredProducts().slice(0, endIndex);
  });

  hasMoreProducts = computed(() => {
    return this.displayedProducts().length < this.filteredProducts().length;
  });

  // Methods
  ngOnInit(): void {
    // Reset to first page when component initializes
    this.currentPage.set(1);
  }

  ngOnDestroy(): void {
    // Cleanup if needed
  }

  ngOnChanges(changes: SimpleChanges): void {
    console.log(changes);
  }

  @HostListener('window:scroll')
  onScroll(): void {
    // Auto-load more products when user is near bottom
    if (this.shouldAutoLoad()) {
      this.loadMoreProducts();
    }
  }

  private shouldAutoLoad(): boolean {
    if (this.isLoading() || !this.hasMoreProducts()) {
      return false;
    }

    const scrollPosition = window.scrollY + window.innerHeight;
    const documentHeight = document.documentElement.scrollHeight;

    // Load more when user is 200px from bottom
    return scrollPosition >= documentHeight - 200;
  }

  setViewMode(mode: 'grid' | 'list'): void {
    this.viewMode.set(mode);
  }

  onFiltersChanged(filters: Record<string, string[]>): void {
    this.activeFilters.set(filters);
    this.currentPage.set(1); // Reset to first page
  }

  onPriceFiltersChanged(priceFilters: { min?: number; max?: number }): void {
    this.priceFilters.set(priceFilters);
    this.currentPage.set(1); // Reset to first page
  }

  onQuickFilterSelected(value: string | null): void {
    this.selectedQuickFilter.set(value);
    this.currentPage.set(1);
  }

  onSortChange(value: string): void {
    this.sortBy.set(value);
    this.currentPage.set(1); // Reset to first page
  }

  loadMoreProducts(): void {
    if (this.isLoading() || !this.hasMoreProducts()) {
      return;
    }

    this.isLoading.set(true);

    // Simulate loading delay for better UX
    setTimeout(() => {
      this.currentPage.set(this.currentPage() + 1);
      this.isLoading.set(false);
    }, 300);
  }

  private sortProducts(products: Producto[]): Producto[] {
    const sortedProducts = [...products];
    const currentSortBy = this.sortBy();

    switch (currentSortBy) {
      case 'price-low':
        return sortedProducts.sort((a, b) => a.precio - b.precio);
      case 'price-high':
        return sortedProducts.sort((a, b) => b.precio - a.precio);
      case 'rating':
        return sortedProducts.sort((a, b) => b.rating - a.rating);
      case 'best-sellers':
        return sortedProducts.sort((a, b) => b.votes - a.votes);
      case 'relevance':
      default:
        return sortedProducts;
    }
  }

  clearAllFilters(): void {
    this.activeFilters.set({});
    this.priceFilters.set({});
    this.selectedQuickFilter.set(null);
    this.currentPage.set(1);
  }

  clearQuickFilters(): void {
    this.activeFilters.set({});
    this.priceFilters.set({});
    this.selectedQuickFilter.set(null);
    this.currentPage.set(1);
  }

  hasActiveQuickFilters(): boolean {
    return (
      this.selectedQuickFilter() !== null ||
      Object.values(this.activeFilters()).some((values) => values.length > 0)
    );
  }

  hasDefaultVehicle = computed(() => {
    const defaultVehicle = this.vehicleService.getDefaultVehicle();
    // Si cambia el vehículo, limpiar el cache
    if (defaultVehicle && this.lastVehicleId !== defaultVehicle.id) {
      this.lastVehicleId = defaultVehicle.id;
    }
    return !!defaultVehicle;
  });

  private lastVehicleId: number | undefined;

  /**
   * Obtiene el total de productos disponibles considerando la compatibilidad
   */
  getTotalAvailableProducts(): number {
    return this.products.length;
  }

  /**
   * Obtiene información del vehículo por defecto para mostrar
   */
  getDefaultVehicleInfo(): string {
    const defaultVehicle = this.vehicleService.getDefaultVehicle();
    if (!defaultVehicle) return '';

    return `${defaultVehicle.anio} ${defaultVehicle.marca} ${defaultVehicle.modelo}`;
  }

  // Métodos auxiliares para filtrado basado en atributos

  /**
   * Verifica si un producto cumple con un filtro de atributo
   */
  private productMatchesAttributeFilter(
    product: Producto,
    filterKey: string,
    filterValues: string[]
  ): boolean {
    if (!product.atributos || !Array.isArray(product.atributos)) {
      return false;
    }

    // Buscar el atributo que corresponde a este filtro
    const matchingAttribute = product.atributos.find(
      (atributo) => this.sanitizeKey(atributo.label) === filterKey
    );

    if (!matchingAttribute) {
      return false;
    }

    const productValue = String(matchingAttribute.value).trim();
    return filterValues.some(
      (filterValue) => productValue === filterValue.trim()
    );
  }

  private productMatchesBrandFilter(
    product: Producto,
    filterValues: string[]
  ): boolean {
    return filterValues.includes(product.marca);
  }

  /**
   * Sanitiza el label del atributo para crear una key única
   */
  private sanitizeKey(label: string): string {
    return label
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '_')
      .replace(/_+/g, '_')
      .replace(/^_|_$/g, '');
  }

  /**
   * Encuentra atributos que coincidan con un valor de búsqueda
   */
  private findMatchingAttributes(
    searchValue: string
  ): Array<{ label: string; value: string }> {
    const matchingAttributes: Array<{ label: string; value: string }> = [];
    const searchLower = searchValue.toLowerCase().trim();

    this.products.forEach((product) => {
      if (product.atributos && Array.isArray(product.atributos)) {
        product.atributos.forEach((atributo) => {
          if (atributo.label && atributo.value) {
            const labelMatch = atributo.label
              .toLowerCase()
              .includes(searchLower);
            const valueMatch = String(atributo.value)
              .toLowerCase()
              .includes(searchLower);

            if (labelMatch || valueMatch) {
              // Evitar duplicados
              const exists = matchingAttributes.some(
                (attr) =>
                  attr.label === atributo.label &&
                  attr.value === String(atributo.value)
              );

              if (!exists) {
                matchingAttributes.push({
                  label: atributo.label,
                  value: String(atributo.value),
                });
              }
            }
          }
        });
      }
    });

    return matchingAttributes;
  }
}
